import React, { useState } from "react";
import "./App.css";

const LANG_OPTIONS = [
  { value: "auto", label: "自動検出" },
  { value: "ja", label: "日本語" },
  { value: "en", label: "英語" },
  { value: "zh", label: "中国語" },
  { value: "ko", label: "韓国語" },
  // 必要に応じて増やす
];

// 自動改行用の整形関数
const applyAutoBreak = (text) => {
  if (!text) return "";
  return text
    .replace(/。\s*/g, "。\n") // 日本語の句点
    .replace(/\.\s*/g, ".\n"); // 英語のピリオド
};

function App() {
  const [sourceLang, setSourceLang] = useState("auto");
  const [targetLang, setTargetLang] = useState("en");
  const [inputText, setInputText] = useState("");
  const [rawOutputText, setRawOutputText] = useState(""); // 素の翻訳結果
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [autoLineBreak, setAutoLineBreak] = useState(true); // 自動改行ON/OFF

  // アライメント関連
  const [alignment, setAlignment] = useState([]); // [{s, t}, ...]
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [isAligning, setIsAligning] = useState(false);
  const [isEditingInput, setIsEditingInput] = useState(true);

  const handleSwapLang = () => {
    if (sourceLang === "auto") {
      setSourceLang(targetLang);
      setTargetLang("ja");
    } else {
      const newSource = targetLang;
      const newTarget = sourceLang;
      setSourceLang(newSource);
      setTargetLang(newTarget);
    }
    // テキストも入れ替え
    setInputText(rawOutputText);
    setRawOutputText(inputText);
    setAlignment([]);
    setHoveredIndex(null);
    setIsEditingInput(true);
  };

  // アライメント取得（翻訳完了後に非同期で呼ぶ）
  const fetchAlignment = async (sourceText, translatedText) => {
    setIsAligning(true);
    try {
      const res = await fetch("/api/align", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sourceText, translatedText }),
      });
      const data = await res.json();
      if (data?.alignment && Array.isArray(data.alignment)) {
        setAlignment(data.alignment);
        setIsEditingInput(false);
      }
    } catch (err) {
      console.error("アライメント取得失敗:", err);
      // アライメント失敗は翻訳には影響しないので無視
    } finally {
      setIsAligning(false);
    }
  };

  const handleTranslate = async () => {
    setErrorMessage("");
    setRawOutputText("");
    setAlignment([]);
    setHoveredIndex(null);
    setIsEditingInput(true);

    if (!inputText.trim()) {
      setErrorMessage("翻訳するテキストを入力してください。");
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch("/api/translate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          text: inputText,
          sourceLang,
          targetLang,
        }),
      });

      let data = null;
      try {
        data = await res.json();
      } catch {
        // JSON でないレスポンスの場合は無視
      }

      if (!res.ok) {
        console.error("API error:", res.status, data);
        const serverError =
          data && (data.error || data.detail)
            ? `サーバーエラー: ${data.error || data.detail}`
            : `サーバーエラーが発生しました (status=${res.status})`;
        setErrorMessage(serverError);
        return;
      }

      const translated = data?.translatedText || "";
      setRawOutputText(translated);

      // 翻訳完了後、非同期でアライメント取得
      if (translated) {
        fetchAlignment(inputText, translated);
      }
    } catch (err) {
      console.error(err);
      setErrorMessage("ネットワークまたはサーバーエラーが発生しました。");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = () => {
    const textToCopy = autoLineBreak
      ? applyAutoBreak(rawOutputText)
      : rawOutputText;

    if (!textToCopy) return;

    navigator.clipboard.writeText(textToCopy).catch((e) => {
      console.error("クリップボードへのコピーに失敗しました", e);
    });
  };

  // 画面に表示するテキスト（自動改行ONなら整形）
  const displayOutputText = autoLineBreak
    ? applyAutoBreak(rawOutputText)
    : rawOutputText;

  // 入力パネルのクリックでテキストエリアに戻す
  const handleInputOverlayClick = () => {
    setIsEditingInput(true);
  };

  // アライメントがあるか
  const hasAlignment = alignment.length > 0;

  // ソース側のセグメント表示
  const renderSourceSegments = () => (
    <div
      className="segment-view"
      onClick={handleInputOverlayClick}
      title="クリックして編集"
    >
      {alignment.map((seg, i) => (
        <span
          key={i}
          className={`segment source-segment${hoveredIndex === i ? " segment-highlighted" : ""}`}
        >
          {seg.s}
        </span>
      ))}
    </div>
  );

  // ターゲット側のセグメント表示
  const renderTargetSegments = () => (
    <div className="segment-view">
      {alignment.map((seg, i) => (
        <span
          key={i}
          className={`segment target-segment${hoveredIndex === i ? " segment-highlighted" : ""}`}
          onMouseEnter={() => setHoveredIndex(i)}
          onMouseLeave={() => setHoveredIndex(null)}
        >
          {autoLineBreak ? applyAutoBreak(seg.t) : seg.t}
        </span>
      ))}
    </div>
  );

  return (
    <div className="app-root">
      <header className="app-header">
        <h1>Sanko-Translator</h1>
      </header>

      <main className="app-main">
        {/* 言語選択行 */}
        <div className="lang-row">
          <select
            value={sourceLang}
            onChange={(e) => setSourceLang(e.target.value)}
          >
            {LANG_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          <button
            type="button"
            className="swap-button"
            onClick={handleSwapLang}
          >
            ⇄
          </button>

          <select
            value={targetLang}
            onChange={(e) => setTargetLang(e.target.value)}
          >
            {LANG_OPTIONS.filter((l) => l.value !== "auto").map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* テキストエリア 2 ペイン */}
        <div className="panel-row">
          <div className="panel">
            <div className="output-header">&nbsp;</div>
            {hasAlignment && !isEditingInput ? (
              renderSourceSegments()
            ) : (
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                placeholder="ここに翻訳したいテキストを入力してください"
              />
            )}
          </div>

          <div className="panel">
            {/* 翻訳結果側のヘッダー＋チェックボックス */}
            <div className="output-header">
              <span>
                翻訳結果
                {isAligning && (
                  <span className="aligning-indicator"> (対応分析中...)</span>
                )}
              </span>
              <label className="auto-break-toggle">
                <input
                  type="checkbox"
                  checked={autoLineBreak}
                  onChange={(e) => setAutoLineBreak(e.target.checked)}
                />
                自動改行する
              </label>
            </div>
            {hasAlignment ? (
              renderTargetSegments()
            ) : (
              <textarea
                value={displayOutputText}
                readOnly
                placeholder="翻訳結果がここに表示されます"
              />
            )}
          </div>
        </div>

        {/* ボタン行 */}
        <div className="button-row">
          <button type="button" onClick={handleTranslate} disabled={isLoading}>
            {isLoading ? "翻訳中..." : "翻訳"}
          </button>
          <button
            type="button"
            onClick={handleCopy}
            disabled={!rawOutputText || isLoading}
          >
            コピー
          </button>
        </div>

        {/* エラー表示 */}
        {errorMessage && <div className="error-message">{errorMessage}</div>}
      </main>
    </div>
  );
}

export default App;
